import React from 'react';
import { HOLOLAB_COLORS, HOLOLAB_FONTS } from '../../constants';

// ─── Tutorial Steps ────────────────────────────────────────────────────────────

interface TutorialStepData {
  title: string;
  body: string;
  icon: string;
}

const TUTORIAL_STEPS: TutorialStepData[] = [
  {
    title: 'Welcome to Experiment A!',
    body: "You'll perform a virtual chemistry experiment: Zinc reacting with Sulphuric Acid. This is a classic experiment from the school curriculum. Follow the guided steps to collect real data!",
    icon: '🧪',
  },
  {
    title: 'Interactive 3D Apparatus',
    body: 'The left panel shows a 3D model of the experimental apparatus. You can rotate the view by clicking and dragging, and zoom in/out with the scroll wheel.',
    icon: '🔭',
  },
  {
    title: 'Follow the Procedure',
    body: 'The right panel guides you through the experiment steps one by one. Each step has a description and an action button. You cannot skip ahead — complete each step in order.',
    icon: '📋',
  },
  {
    title: 'Record Your Readings',
    body: "Watch the inverted burette in the 3D scene. As the reaction proceeds, the water level drops as H₂ gas is collected. When a column flashes amber in Table 7.1 below, it's time to record that reading.",
    icon: '📊',
  },
  {
    title: 'Submit & Check Your Work',
    body: 'After the experiment completes, fill in all values in Table 7.1 and answer the Discussion Questions. Click Submit to check your answers. Green ✓ means correct, red ✗ means incorrect — with the model answer shown.',
    icon: '✅',
  },
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExperimentTutorialProps {
  tutorialStep: number;
  onNext: () => void;
  onSkip: () => void;
}

// ─── Dot Indicator ─────────────────────────────────────────────────────────────

const DotIndicator: React.FC<{ total: number; current: number }> = ({ total, current }) => (
  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        style={{
          width: i === current ? '20px' : '8px',
          height: '8px',
          borderRadius: '4px',
          background: i === current ? HOLOLAB_COLORS.precisionBlue : 'rgba(10,25,41,0.15)',
          transition: 'width 250ms cubic-bezier(0.4, 0, 0.2, 1), background 250ms',
        }}
      />
    ))}
  </div>
);

// ─── Tutorial Modal ────────────────────────────────────────────────────────────

const ExperimentTutorial: React.FC<ExperimentTutorialProps> = ({ tutorialStep, onNext, onSkip }) => {
  const step = TUTORIAL_STEPS[tutorialStep];
  const isLast = tutorialStep === TUTORIAL_STEPS.length - 1;

  return (
    /* Backdrop */
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(10, 25, 41, 0.55)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
      // Allow clicking backdrop to skip
      onClick={onSkip}
    >
      {/* Card */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: HOLOLAB_COLORS.labWhite,
          border: '1px solid rgba(10,25,41,0.08)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(10,25,41,0.16)',
          padding: '36px',
          maxWidth: '460px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          animation: 'tutFadeIn 0.25s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {/* Icon + Progress */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '14px',
              background: 'rgba(46,92,138,0.08)',
              border: '1px solid rgba(46,92,138,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
            }}
          >
            {step.icon}
          </div>
          <button
            onClick={onSkip}
            aria-label="Skip tutorial"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontFamily: HOLOLAB_FONTS.body,
              fontSize: '13px',
              color: 'rgba(10,25,41,0.4)',
              padding: '4px 0',
              transition: 'color 150ms',
            }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.color = 'rgba(10,25,41,0.7)')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.color = 'rgba(10,25,41,0.4)')}
          >
            Skip tutorial
          </button>
        </div>

        {/* Content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <h2
            style={{
              fontFamily: HOLOLAB_FONTS.heading,
              fontSize: '20px',
              fontWeight: 700,
              color: HOLOLAB_COLORS.deepBlue,
              margin: 0,
              lineHeight: 1.3,
            }}
          >
            {step.title}
          </h2>
          <p
            style={{
              fontFamily: HOLOLAB_FONTS.body,
              fontSize: '14px',
              color: 'rgba(10,25,41,0.65)',
              margin: 0,
              lineHeight: 1.7,
            }}
          >
            {step.body}
          </p>
        </div>

        {/* Footer: dots + button */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <DotIndicator total={TUTORIAL_STEPS.length} current={tutorialStep} />
          <button
            onClick={onNext}
            style={{
              background: HOLOLAB_COLORS.precisionBlue,
              color: HOLOLAB_COLORS.labWhite,
              border: 'none',
              borderRadius: '8px',
              padding: '10px 24px',
              fontFamily: HOLOLAB_FONTS.body,
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'opacity 150ms',
            }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = '0.85')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = '1')}
          >
            {isLast ? "Let's Start!" : 'Next →'}
          </button>
        </div>
      </div>

      {/* Animation keyframe */}
      <style>{`
        @keyframes tutFadeIn {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export { TUTORIAL_STEPS };
export default ExperimentTutorial;
